import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  Tooltip,
  Modal,
  useMediaQuery,
} from "@mui/material";
import { useAppConfig } from "../../context/AppConfigContext";
import api from "../../services/api";

const MobileNameModal = ({ open, handleClose, name }) => (
  <Modal open={open} onClose={handleClose}>
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        bgcolor: "background.paper",
        borderRadius: 2,
        p: 3,
        boxShadow: 24,
        textAlign: "center",
        minWidth: 250,
      }}>
      <Typography variant="h6" fontWeight="bold">
        {name}
      </Typography>
    </Box>
  </Modal>
);

const FilterSidebar = ({ filters, setFilters }) => {
  const [attributeOptions, setAttributeOptions] = useState({
    decor: [],
    style: [],
    texture: [],
  });
  const [mobileModalOpen, setMobileModalOpen] = useState(false);
  const [selectedName, setSelectedName] = useState("");
  const isMobile = useMediaQuery("(max-width:600px)");
  const { config } = useAppConfig();
  const eventTypes = config?.eventTypes?.filter((et) => et.isActive) || [];
  const longPressTimeout = useRef();

  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        const response = await api.get("/cake-attributes");
        const styles = response.data.filter((attr) => attr.type === "style");
        const textures = response.data.filter(
          (attr) => attr.type === "texture"
        );
        const decors = response.data.filter((attr) => attr.type === "decor");
        setAttributeOptions({
          style: styles,
          texture: textures,
          decor: decors,
        });
      } catch (error) {
        console.error("Failed to fetch cake attributes:", error);
      }
    };
    fetchAttributes();
  }, []);

  const handleRecipientChange = (recipient) => {
    setFilters((prev) => ({
      ...prev,
      recipient: prev.recipient === recipient ? "All" : recipient,
    }));
  };

  const handleEventTypeChange = (eventType) => {
    setFilters((prev) => ({
      ...prev,
      eventType: prev.eventType === eventType.key ? "All" : eventType.key,
    }));
  };

  const handleStyleChange = (style) => {
    setFilters((prev) => ({
      ...prev,
      style: prev.style === style ? "" : style,
    }));
  };

  const handleDecorChange = (decor) => {
    setFilters((prev) => {
      const updatedDecor = prev.decor.includes(decor)
        ? prev.decor.filter((d) => d !== decor)
        : [...prev.decor, decor];
      return { ...prev, decor: updatedDecor };
    });
  };

  const handleStartPress = (name) => {
    if (isMobile) {
      longPressTimeout.current = setTimeout(() => {
        setSelectedName(name);
        setMobileModalOpen(true);
      }, 500);
    }
  };

  const handleEndPress = () => {
    clearTimeout(longPressTimeout.current);
  };

  const renderAttributeChip = (option, isSelected, onSelect) => (
    <Tooltip title={!isMobile ? option.name : ""} arrow placement="top">
      <Chip
        label={option.name}
        clickable
        onClick={onSelect}
        onTouchStart={() => handleStartPress(option.name)}
        onTouchEnd={handleEndPress}
        onMouseDown={handleEndPress}
        onMouseUp={handleEndPress}
        sx={{
          backgroundColor: isSelected ? "rgba(253, 119, 98, 0.15)" : "grey.100",
          color: isSelected ? "#FD7762" : "text.primary",
          border: "1px solid",
          borderColor: isSelected ? "#FD7762" : "grey.300",
          maxWidth: "120px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: "24px",
            height: "100%",
            background:
              "linear-gradient(to right, rgba(255,255,255,0), #f5f5f5 90%)",
          },
          "&:hover": {
            backgroundColor: isSelected
              ? "rgba(253, 119, 98, 0.25)"
              : "rgba(0,0,0,0.04)",
          },
        }}
      />
    </Tooltip>
  );

  return (
    <Box
      sx={{
        p: { xs: 1, md: 2 },
        mr: { xs: 0, md: 4 },
        overflowY: "auto",
        position: "relative",
      }}>
      {/* Recipient */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight="bold" mb={1}>
          Recipient
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {["All", "For Women", "For Men", "For Kids"].map((option) => (
            <Chip
              key={option}
              label={option}
              clickable
              onClick={() => handleRecipientChange(option)}
              sx={{
                backgroundColor:
                  filters.recipient === option
                    ? "rgba(253, 119, 98, 0.15)"
                    : "grey.100",
                color:
                  filters.recipient === option ? "#FD7762" : "text.primary",
                border: "1px solid",
                borderColor:
                  filters.recipient === option ? "#FD7762" : "grey.300",
                maxWidth: "120px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                position: "relative",
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Event Type */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight="bold" mb={1}>
          Event Type
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 1,
          }}>
          {eventTypes.map((option) => (
            <Button
              key={option.key}
              onClick={() => handleEventTypeChange(option)}
              size="small"
              sx={{
                textTransform: "none",
                fontWeight: 500,
                backgroundColor:
                  filters.eventType === option.key
                    ? "rgba(253, 119, 98, 0.15)"
                    : "transparent",
                color:
                  filters.eventType === option.key ? "#FD7762" : "text.primary",
                border: "1px solid",
                borderColor:
                  filters.eventType === option.key ? "#FD7762" : "grey.300",
                "&:hover": {
                  backgroundColor:
                    filters.eventType === option.key
                      ? "rgba(253, 119, 98, 0.25)"
                      : "rgba(0,0,0,0.04)",
                },
              }}>
              {option.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Style */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight="bold" mb={1}>
          Style
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 1,
          }}>
          {attributeOptions.style.map((option) =>
            renderAttributeChip(option, filters.style === option.name, () =>
              handleStyleChange(option.name)
            )
          )}
        </Box>
      </Box>

      {/* Decor */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight="bold" mb={1}>
          Decor
        </Typography>
        {isMobile && (
          <Box
            sx={{
              backgroundColor: "rgba(253, 119, 98, 0.1)",
              borderRadius: 1,
              p: 1,
              mb: 1,
            }}>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center">
              Tap to select, long-press to view full name.
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 1,
          }}>
          {attributeOptions.decor.map((option) =>
            renderAttributeChip(
              option,
              filters.decor.includes(option.name),
              () => handleDecorChange(option.name)
            )
          )}
        </Box>
      </Box>

      {/* Mobile Modal */}
      <MobileNameModal
        open={mobileModalOpen}
        handleClose={() => setMobileModalOpen(false)}
        name={selectedName}
      />
    </Box>
  );
};

export default FilterSidebar;
